import { create } from "zustand";
import toast from "react-hot-toast";
import { RepositoryManager } from "../../data/repositoryManager";

export interface Announcement {
    id: number;
    title: string;
    content: string;
    type: "banner" | "popup" | "text";
    position: "home";
    status: "active" | "inactive";
    priority: number;
    start_at: string | null;
    end_at: string | null;
    created_at: string;
    updated_at: string;
}

interface PaginationLinks {
    url: string | null;
    label: string;
    page: number | null;
    active: boolean;
}

interface AnnouncementsDataResponse {
    current_page: number;
    data: Announcement[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: PaginationLinks[];
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
}

interface AnnouncementDataResponse {
    data: Announcement;
}

interface AnnouncementsStoreState {
    announcementsData: AnnouncementsDataResponse | null;
    announcementData: AnnouncementDataResponse | null;
    error: string | null;

    isListLoading: boolean;
    isDetailLoading: boolean;
    isSaving: boolean;
    isDeleting: boolean;

    getAnnouncements: (params?: any) => Promise<{ success: boolean; data?: AnnouncementsDataResponse; error?: string }>;
    getAnnouncementById: (announcementId: string | number) => Promise<{ success: boolean; data?: AnnouncementDataResponse; error?: string }>;
    createAnnouncement: (payload: Partial<Announcement>) => Promise<{ success: boolean; data?: Announcement; error?: string }>;
    updateAnnouncement: (announcementId: string | number, payload: Partial<Announcement>) => Promise<{ success: boolean; data?: Announcement; error?: string }>;
    deleteAnnouncement: (announcementId: string | number) => Promise<{ success: boolean; error?: string }>;
}

const useAnnouncementsStore = create<AnnouncementsStoreState>((set, get) => ({
    announcementsData: null,
    announcementData: null,
    error: null,

    isListLoading: false,
    isDetailLoading: false,
    isSaving: false,
    isDeleting: false,

    getAnnouncements: async (params = {}) => {
        set({ isListLoading: true, error: null });
        try {
            const result = await RepositoryManager.announcements.getAnnouncements(params);
            if (result.success && result.data) {
                // Transform API response to match expected structure
                const transformedData = {
                    current_page: result.data.current_page || 1,
                    data: result.data.data || [],
                    first_page_url: "",
                    from: ((result.data.current_page || 1) - 1) * (result.data.per_page || 30) + 1,
                    last_page: result.data.last_page || 1,
                    last_page_url: "",
                    links: [],
                    next_page_url: null,
                    path: "",
                    per_page: result.data.per_page || 30,
                    prev_page_url: null,
                    to: (result.data.current_page || 1) * (result.data.per_page || 30),
                    total: result.data.total || 0,
                };
                set({ announcementsData: transformedData, isListLoading: false });
                return { success: true, data: transformedData };
            }
            throw new Error(result.message || "Failed to fetch announcements");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isListLoading: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    getAnnouncementById: async (announcementId) => {
        set({ isDetailLoading: true, error: null });
        try {
            const result = await RepositoryManager.announcements.getAnnouncementById(announcementId);
            if (result.success && result.data) {
                set({ announcementData: { data: result.data }, isDetailLoading: false });
                return { success: true, data: { data: result.data } };
            }
            throw new Error(result.message || "Failed to fetch announcement details");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            set({ isDetailLoading: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    createAnnouncement: async (payload) => {
        set({ isSaving: true, error: null });
        try {
            const result = await RepositoryManager.announcements.createAnnouncement(payload);
            if (result.success && result.data) {
                const newAnnouncement = result.data as Announcement;
                const { announcementsData } = get();
                if (announcementsData) {
                    const newData = {
                        ...announcementsData,
                        data: [newAnnouncement, ...announcementsData.data],
                        total: announcementsData.total + 1,
                    };
                    set({ announcementsData: newData });
                }
                toast.success("Announcement created successfully ✅");
                set({ isSaving: false });
                return { success: true, data: newAnnouncement };
            }
            throw new Error(result.message || "Failed to create announcement");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Error creating announcement";
            set({ isSaving: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    updateAnnouncement: async (announcementId, payload) => {
        set({ isSaving: true, error: null });
        try {
            const result = await RepositoryManager.announcements.updateAnnouncement(announcementId, payload);
            if (result.success && result.data) {
                const updatedAnnouncement = result.data as Announcement;
                const { announcementsData } = get();
                if (announcementsData) {
                    const newData = {
                        ...announcementsData,
                        data: announcementsData.data.map((a) => (a.id === updatedAnnouncement.id ? updatedAnnouncement : a)),
                    };
                    set({ announcementsData: newData });
                }
                toast.success("Announcement updated successfully ✅");
                set({ isSaving: false });
                return { success: true, data: updatedAnnouncement };
            }
            throw new Error(result.message || "Failed to update announcement");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Error updating announcement";
            set({ isSaving: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },

    deleteAnnouncement: async (announcementId) => {
        set({ isDeleting: true, error: null });
        try {
            const result = await RepositoryManager.announcements.deleteAnnouncement(announcementId);
            if (result.success) {
                const { announcementsData } = get();
                if (announcementsData) {
                    set({
                        announcementsData: {
                            ...announcementsData,
                            data: announcementsData.data.filter((a) => a.id !== announcementId),
                            total: announcementsData.total - 1,
                        },
                    });
                }
                toast.success("Announcement deleted successfully 🗑");
                set({ isDeleting: false });
                return { success: true };
            }
            throw new Error(result.message || "Failed to delete announcement");
        } catch (err) {
            const message = err instanceof Error ? err.message : "Error deleting announcement";
            set({ isDeleting: false, error: message });
            toast.error(message, { icon: "❌" });
            return { success: false, error: message };
        }
    },
}));

export default useAnnouncementsStore;

